<template>
  <screening-workflow-panel
    :review="review"
    :current-researcher="currentResearcher"
    :pending-connection.sync="referencesScreenedConnection"
    :pending-connection-query="
      $apollo.queries.referencesScreenedConnection
    "
    :search-input.sync="searchInput"
    :get-vote-by-current-user-func="getOutcomeByCurrentUser"
    :revoke-vote-func="onRevokeOutcome"
    :should-hide-reference-func="shouldHideReference"
    :should-hide-reference-based-on-vote-func="
      shouldHideReferenceBasedOnOutcome
    "
    :filtering-input="filteringInput"
    disable-select-next-on-vote
    @saving-vote="onSavingOutcome"
    @clear-filtering-input="$emit('clear-filtering-input')"
  >
    <template #empty-state>
      <screen-tab-complete-empty-state />
    </template>
  </screening-workflow-panel>
</template>

<script>
import ScreeningWorkflowPanel from './ScreeningWorkflowPanel';
import ScreenTabCompleteEmptyState from './ScreenTabCompleteEmptyState';
import { GetReferencesScreened } from '@/graphql';
import {
  Review,
  Researcher,
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';
import {
  RelevanceAssessment,
  ScreeningOutcomeResolution,
  AssessmentStatusFilter,
} from '@/enums';
import { v4 as createUUID } from 'uuid';
import { mapActions } from 'vuex';
import { isEqual } from 'lodash';

const PAGE_SIZE = 50;
const ALLOWED_OUTCOMES_TO_FILTERS = {
  [RelevanceAssessment.RELEVANT]:
    AssessmentStatusFilter.RELEVANT_OUTCOME,
  [RelevanceAssessment.NOT_SURE]:
    AssessmentStatusFilter.NOT_SURE_OUTCOME,
  [RelevanceAssessment.NOT_RELEVANT]:
    AssessmentStatusFilter.NOT_RELEVANT_OUTCOME,
};

export default {
  name: 'ScreenTabComplete',
  components: {
    ScreeningWorkflowPanel,
    ScreenTabCompleteEmptyState,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    filterOutcomesTo: {
      type: Array,
      default: () => [
        RelevanceAssessment.RELEVANT,
        RelevanceAssessment.NOT_SURE,
      ],
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clear-filtering-input'],
  data: () => ({
    searchInput: null,
    referencesScreenedConnection: null,
    hiddenReferenceMap: {},
  }),
  apollo: {
    referencesScreenedConnection: {
      query: GetReferencesScreened,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
          searchQuery: this.searchInput || null,
          outcomes: this.filterOutcomesTo.map(
            (outcome) => ALLOWED_OUTCOMES_TO_FILTERS[outcome]
          ),
          hasTag: this.filteringInput.hasTag,
          hasKeyword: this.filteringInput.hasKeyword,
          hasAuthor: this.filteringInput.hasAuthor,
          hasPlacePublished: this.filteringInput.hasPlacePublished,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(data.referencesScreened),
      skip() {
        return !this.review?.id;
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  },
  watch: {
    active(active) {
      if (active && this.referencesScreenedConnection) {
        this.$apollo.queries.referencesScreenedConnection.refetch();
      }
    },
    filteringInput(newInput, oldInput) {
      // Clear the cache when the filtering input changes to show the loading state.
      const changed = !isEqual(newInput, oldInput);
      if (this.active && changed) {
        this.referencesScreenedConnection = null;
      }
    },
  },
  methods: {
    ...mapActions({
      undoManualOverride: 'screeningOutcome/undoManualOverride',
      removeConflictResolution:
        'screeningOutcome/removeConflictResolution',
    }),
    shouldHideReference(reference) {
      return (
        this.hiddenReferenceMap[reference.id] ||
        !this.filterOutcomesTo.includes(
          reference.screeningOutcome?.outcome
        )
      );
    },
    shouldHideReferenceBasedOnOutcome(screeningOutcome) {
      return !this.filterOutcomesTo.includes(
        screeningOutcome.outcome
      );
    },
    getOutcomeByCurrentUser(reference) {
      const screeningOutcome = reference?.screeningOutcome;
      if (!screeningOutcome || !screeningOutcome.id) return null;

      const allowedStates = [
        ScreeningOutcomeResolution.UPDATED_OUTCOME,
        ScreeningOutcomeResolution.CONFLICT_RESOLUTION,
      ];
      const resolvedBy = screeningOutcome.resolvedBy?.researcher.id;
      const researcherId = this.currentResearcher.id;

      return (
        resolvedBy === researcherId &&
        allowedStates.includes(screeningOutcome.resolution) &&
        screeningOutcome
      );
    },
    onSavingOutcome(screeningOutcome) {
      // Instantly hide the reference if the outcome will result in it being hidden.
      this.$set(
        this.hiddenReferenceMap,
        screeningOutcome.reference.id,
        !this.filterOutcomesTo.includes(screeningOutcome.outcome)
      );
    },
    onRevokeOutcome({ id, cacheUpdate, reference }) {
      const screeningOutcome = this.getOutcomeByCurrentUser(
        reference
      );
      if (!screeningOutcome)
        throw new Error(
          'No screening outcome by the current user to revoke'
        );

      const lastArchivedOutcome = reference.getLastArchivedScreeningOutcome();
      const previousOutcomeNotInThisPage =
        lastArchivedOutcome &&
        !this.filterOutcomesTo.includes(lastArchivedOutcome.outcome);

      switch (screeningOutcome.resolution) {
        case ScreeningOutcomeResolution.UPDATED_OUTCOME:
          // Hide the reference is the previous outcome (that is about to become current)
          // would not be in this page.
          this.$set(
            this.hiddenReferenceMap,
            reference.id,
            previousOutcomeNotInThisPage
          );
          return this.undoManualOverride({
            id,
            cacheUpdate: (cache, data) => {
              cacheUpdate(cache, data);

              // Set the previous outcome to the reference.screeningOutcome
              // cache as an optimistic response. Which should result in the
              // dropdown showing the previous outcome but show a loading
              // indicator until the actual response comes back.
              if (!data.optimisticId) return;
              this.setOptimisticOutcomeOnReference(
                cache,
                reference,
                lastArchivedOutcome
              );
            },
            optimisticResponse: {
              ...screeningOutcome,
              reference,
            },
            reference,
          });
        case ScreeningOutcomeResolution.CONFLICT_RESOLUTION:
          // The reference will have no screening outcome after this, so we must
          // hide it now.
          this.$set(this.hiddenReferenceMap, reference.id, true);
          return this.removeConflictResolution({
            id,
            reference,
            cacheUpdate,
          });
        default:
          throw new Error(
            `Unexpected screening outcome resolution to revoke: ${screeningOutcome.resolution}`
          );
      }
    },
    setOptimisticOutcomeOnReference(cache, reference, outcome) {
      cache.modify({
        id: cache.identify(reference),
        fields: {
          screeningOutcome(_, { toReference }) {
            return toReference(
              {
                id: outcome.id,
                optimisticId: createUUID(),
                __typename: 'ScreeningOutcome',
              },
              true // merge the above data with the existing data in the cache
            );
          },
        },
      });
    },
  },
};
</script>
